import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import moment from "moment";
import { API } from "../../constAPI";
import { isAuthenticated } from "../apicalls/auth";
import Spinner from "./Spinner";
import Paginations from "./Paginations";
import "./style/UpcomingLessons.css";
import { AiOutlineClockCircle } from "react-icons/ai";
import {
  holdsLessonSpot,
  resolveBookingPaymentState,
} from "../utils/bookingPaymentState";

const STATUS_OPTIONS = [
  { value: "PENDING", label: "Pending" },
  { value: "CONFIRMED", label: "Confirmed" },
  { value: "CANCELLED", label: "Cancelled" },
];

const STATUS_BY_ID = {
  0: "PENDING",
  1: "CONFIRMED",
  2: "CANCELLED",
};

const LESSON_LEVELS = [
  "All",
  "Beginner (NTRP 2.5)",
  "Advanced Beginner (NTRP 3.0)",
  "Intermediate (NTRP 3.5)",
  "Advanced (NTRP 4.0)",
  "Advanced Plus (NTRP 4.5)",
  "Expert (NTRP 5.0)",
];

const getTitle = (lesson) => lesson.metadata?.title || lesson.title || "-";
const getCoachName = (lesson) => lesson.coach_name || lesson.coachName || lesson.full_name || "-";
const getLocation = (lesson) => lesson.location_name || lesson.location || "-";
const getLocationId = (location) => location.id || location.location_id;
const getLocationName = (location) => location.location || location.location_name || location.name || "";
const getGroupPlayers = (lesson) =>
  Array.isArray(lesson?.group_players)
    ? lesson.group_players
    : Array.isArray(lesson?.groupPlayers)
      ? lesson.groupPlayers
      : [];
const getPlayerName = (player, index) =>
  player.full_name || player.name || player.player_name || `Player ${index + 1}`;
const getPlayerPaymentMethod = (player) => player.payment_method || player.paymentMethod;
const getPlayerPaymentStatus = (player) => player.payment_status ?? player.paymentStatus;
const getParticipantState = (player) =>
  resolveBookingPaymentState({
    status: player.status,
    paymentStatus: getPlayerPaymentStatus(player),
    paymentMethod: getPlayerPaymentMethod(player),
  });
const getHeldSpots = (lesson) =>
  getGroupPlayers(lesson).filter((player) =>
    holdsLessonSpot({
      status: player.status,
      paymentStatus: getPlayerPaymentStatus(player),
      paymentMethod: getPlayerPaymentMethod(player),
    })
  ).length;
const getParticipantSummary = (lesson) => {
  const players = getGroupPlayers(lesson);
  const limit = Number(lesson?.player_limit);
  const held = getHeldSpots(lesson);

  if (!players.length && !Number.isFinite(limit)) return "-";
  if (Number.isFinite(limit) && limit > 0) return `${held} held / ${limit} spots`;
  return `${held} held / ${players.length} participants`;
};
const getStatusValue = (status) => {
  if (typeof status === "number") return STATUS_BY_ID[status] || "PENDING";
  if (!status) return "PENDING";
  return String(status).toUpperCase();
};
const parseLessonWallTime = (value) => {
  if (!value) return null;

  const parsed = moment.utc(value);
  return parsed.isValid() ? parsed : null;
};
const getLessonStartValue = (lesson) =>
  lesson.start_date_time || lesson.startDateTime || lesson.start_date_time_tz || lesson.startDateTimeTz;
const getLessonEndValue = (lesson) =>
  lesson.end_date_time || lesson.endDateTime || lesson.end_date_time_tz || lesson.endDateTimeTz;
const getDateTimeValue = (value) => {
  const parsed = parseLessonWallTime(value);
  return parsed ? parsed.format("YYYY-MM-DDTHH:mm") : "";
};
const getEndDateTimeInputValue = (lesson) => {
  const endValue = getDateTimeValue(getLessonEndValue(lesson));
  if (endValue) return endValue;

  const start = parseLessonWallTime(getLessonStartValue(lesson));
  return start ? start.clone().add(1, "hour").format("YYYY-MM-DDTHH:mm") : "";
};
const formatLocalIso = (date) => {
  const pad = (part) => String(part).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:00`;
};
const toWallClockIso = (value) => new Date(`${formatLocalIso(new Date(value))}Z`).toISOString();
const getDurationMinutes = (lesson) => {
  const start = parseLessonWallTime(getLessonStartValue(lesson));
  const end = parseLessonWallTime(getLessonEndValue(lesson));
  return start && end ? String(end.diff(start, "minutes")) : "";
};

const buildLessonForm = (lesson) => ({
  id: lesson.id,
  coach_id: lesson.coach_id,
  lessontype_id: Number(lesson.lessontype_id || 3),
  start: getDateTimeValue(getLessonStartValue(lesson)),
  end: getEndDateTimeInputValue(lesson),
  location_id: lesson.location_id || "",
  location: getLocation(lesson),
  court: lesson.court || "",
  status: getStatusValue(lesson.status),
  title: lesson.metadata?.title || lesson.title || "",
  description: lesson.metadata?.description || "",
  level: lesson.metadata?.level || lesson.level || "All",
  skill_level_required: lesson.metadata?.skill_level_required || lesson.skill_level_required || "",
  duration:
    lesson.metadata?.duration ||
    getDurationMinutes(lesson),
  price_per_person:
    lesson.group_price_per_person !== null && lesson.group_price_per_person !== undefined
      ? String(lesson.group_price_per_person)
      : "",
  player_limit:
    lesson.player_limit !== null && lesson.player_limit !== undefined ? String(lesson.player_limit) : "",
  metadata: lesson.metadata || {},
});

/**
 * Admin page to list upcoming group and external lessons for marketing promotion.
 */
const UpcomingLessons = () => {
  const { access_token } = isAuthenticated() || {};
  const [activeTab, setActiveTab] = useState("all");
  const [groupLessons, setGroupLessons] = useState([]);
  const [externalLessons, setExternalLessons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [lessonForm, setLessonForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirming, setDeleteConfirming] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(15);
  const [totalGroupLessons, setTotalGroupLessons] = useState(0);
  const [totalExternalLessons, setTotalExternalLessons] = useState(0);
  const [coaches, setCoaches] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedCoachId, setSelectedCoachId] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [startDateFilter, setStartDateFilter] = useState("");
  const [endDateFilter, setEndDateFilter] = useState("");
  const [selectedLevel, setSelectedLevel] = useState("All");

  const fetchCoaches = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API}/api/admin/coaches`,
        {
          headers: { Authorization: `Bearer ${access_token}` },
          params: { perPage: 100, page: 1 },
        }
      );
      setCoaches(response.data || []);
    } catch (err) {
      console.error("Failed to fetch coaches:", err);
    }
  }, [access_token]);

  const fetchLocations = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/admin/locations`, {
        headers: { Authorization: `Bearer ${access_token}` },
        params: { perPage: 1000, page: 1, search: "" },
      });
      setLocations(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error("Failed to fetch locations:", err);
      setLocations([]);
    }
  }, [access_token]);

  const fetchLessons = useCallback(async (page = currentPage) => {
    setLoading(true);
    setError("");
    try {
      const url = `${API}/api/admin/lessonshub/upcoming`;
      const body = {
        page,
        perPage,
        merge: false,
        search: searchTerm.trim(),
        filters: {
          ...(startDateFilter
            ? { startDate: moment(startDateFilter, "YYYY-MM-DD").startOf("day").format("YYYY-MM-DD HH:mm:ss") }
            : {}),
          ...(endDateFilter
            ? { endDate: moment(endDateFilter, "YYYY-MM-DD").endOf("day").format("YYYY-MM-DD HH:mm:ss") }
            : {}),
          ...(selectedLevel && selectedLevel !== "All" ? { level: selectedLevel } : {}),
        },
      };
      if (selectedCoachId) {
        body.filters.coachId = Number(selectedCoachId);
      }
      const response = await axios.post(
        url,
        body,
        {
          headers: { Authorization: `Bearer ${access_token}` },
        }
      );
      setGroupLessons(response.data.groupLessons || []);
      setExternalLessons(response.data.externalLessons || []);
      setTotalGroupLessons(response.data.totalGroupLessons || response.data.groupLessons?.length || 0);
      setTotalExternalLessons(response.data.totalExternalLessons || response.data.externalLessons?.length || 0);
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.detail || err.message || "Error fetching lessons");
    } finally {
      setLoading(false);
    }
  }, [access_token, currentPage, perPage, selectedCoachId, searchTerm, startDateFilter, endDateFilter, selectedLevel]);

  const handlePaginate = (page) => {
    setCurrentPage(page);
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, selectedCoachId, perPage, searchTerm, startDateFilter, endDateFilter, selectedLevel]);

  useEffect(() => {
    fetchCoaches();
  }, [fetchCoaches]);

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  useEffect(() => {
    fetchLessons(currentPage);
  }, [fetchLessons, currentPage]);

  const clearFilters = () => {
    setSelectedCoachId("");
    setSearchTerm("");
    setStartDateFilter("");
    setEndDateFilter("");
    setSelectedLevel("All");
    setCurrentPage(1);
  };


  // Merge and sort by start date/time for "All" tab
  const allLessons = [...groupLessons, ...externalLessons].sort(
    (a, b) => {
      const startA = parseLessonWallTime(getLessonStartValue(a));
      const startB = parseLessonWallTime(getLessonStartValue(b));
      return (startA ? startA.valueOf() : 0) - (startB ? startB.valueOf() : 0);
    }
  );
  const lessonsToDisplay =
    activeTab === "group"
      ? groupLessons
      : activeTab === "external"
      ? externalLessons
      : allLessons;

  const formatDateTime = (value, fallbackStartValue) => {
    const parsed = parseLessonWallTime(value);
    if (parsed) return parsed.format("YYYY-MM-DD HH:mm");

    const fallbackStart = parseLessonWallTime(fallbackStartValue);
    return fallbackStart ? fallbackStart.clone().add(1, "hour").format("YYYY-MM-DD HH:mm") : "-";
  };

  const openGroupLesson = (lesson) => {
    setSelectedLesson(lesson);
    setLessonForm(buildLessonForm(lesson));
    setDeleteConfirming(false);
    setSaveError("");
  };

  const closeGroupLesson = () => {
    if (saving || deleting) return;
    setSelectedLesson(null);
    setLessonForm(null);
    setDeleteConfirming(false);
    setSaveError("");
  };

  const handleFormChange = (field, value) => {
    setLessonForm((current) => ({ ...current, [field]: value }));
  };

  const handleLocationChange = (value) => {
    const selectedLocation = locations.find((location) => String(getLocationId(location)) === value);

    setLessonForm((current) => ({
      ...current,
      location_id: value,
      location: selectedLocation ? getLocationName(selectedLocation) : current.location,
    }));
  };

  const saveGroupLesson = async (event) => {
    event.preventDefault();
    if (!lessonForm || deleting) return;

    const startDate = new Date(lessonForm.start);
    const endDate = new Date(lessonForm.end);
    const price = Number(lessonForm.price_per_person);
    const playerLimit = Number(lessonForm.player_limit);
    const locationId = Number(lessonForm.location_id);

    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
      setSaveError("Start and end date/time are required.");
      return;
    }
    if (!locationId) {
      setSaveError("Location ID is required.");
      return;
    }
    if (!Number.isFinite(price) || price <= 0) {
      setSaveError("Price per person must be greater than zero.");
      return;
    }
    if (!Number.isInteger(playerLimit) || playerLimit <= 0) {
      setSaveError("Player limit must be a whole number greater than zero.");
      return;
    }

    const metadata = {
      ...lessonForm.metadata,
      title: lessonForm.title,
      description: lessonForm.description,
      level: lessonForm.level,
      skill_level_required: lessonForm.skill_level_required,
      duration: lessonForm.duration,
    };

    const payload = {
      coach_id: lessonForm.coach_id,
      lessontype_id: lessonForm.lessontype_id,
      start_date_time: toWallClockIso(lessonForm.start),
      end_date_time: toWallClockIso(lessonForm.end),
      start_date_time_tz: toWallClockIso(lessonForm.start),
      end_date_time_tz: toWallClockIso(lessonForm.end),
      location_id: locationId,
      court: lessonForm.court,
      status: lessonForm.status,
      metadata,
      price_per_person: price,
      player_limit: playerLimit,
    };

    if (Number(lessonForm.lessontype_id) === 4) {
      payload.skill_level_required = lessonForm.skill_level_required;
    }

    setSaving(true);
    setSaveError("");
    try {
      await axios.put(`${API}/api/admin/lessons/${lessonForm.id}`, payload, {
        headers: { Authorization: `Bearer ${access_token}` },
      });
      await fetchLessons();
      setSelectedLesson(null);
      setLessonForm(null);
      setSaveError("");
    } catch (err) {
      setSaveError(
        err.response?.data?.detail ||
          err.response?.data?.error ||
          err.message ||
          "Failed to update lesson"
      );
    } finally {
      setSaving(false);
    }
  };

  const deleteGroupLesson = async () => {
    if (!lessonForm) return;

    setDeleting(true);
    setSaveError("");
    try {
      await axios.delete(`${API}/api/admin/lessons/${lessonForm.id}`, {
        headers: { Authorization: `Bearer ${access_token}` },
      });
      await fetchLessons();
      setSelectedLesson(null);
      setLessonForm(null);
      setDeleteConfirming(false);
    } catch (err) {
      setSaveError(
        err.response?.data?.detail ||
          err.response?.data?.error ||
          err.message ||
          "Failed to delete lesson"
      );
    } finally {
      setDeleting(false);
    }
  };

  const locationOptions = lessonForm
    ? [
        ...(!locations.some((location) => String(getLocationId(location)) === String(lessonForm.location_id)) &&
        lessonForm.location_id
          ? [{ id: lessonForm.location_id, location: lessonForm.location }]
          : []),
        ...locations,
      ]
    : locations;

  return (<main>
    <div className="upcoming-container">
      <h2>
        <AiOutlineClockCircle /> Upcoming Lessons
      </h2>
      <div className="upcoming-tabs">
        <button
          className={activeTab === "all" ? "active" : ""}
          onClick={() => setActiveTab("all")}
        >
          All Lessons
        </button>
        <button
          className={activeTab === "group" ? "active" : ""}
          onClick={() => setActiveTab("group")}
        >
          Group Lessons
        </button>
        <button
          className={activeTab === "external" ? "active" : ""}
          onClick={() => setActiveTab("external")}
        >
          External Lessons
        </button>
      </div>
      <div className="upcoming-filter-row">
        <label>
          Search
          <input
            type="search"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Coach or external title"
          />
        </label>
        <label>
          Coach
          <select
            value={selectedCoachId}
            onChange={(event) => setSelectedCoachId(event.target.value)}
          >
            <option value="">All Coaches</option>
            {coaches.map((coach) => (
              <option key={coach.id} value={coach.user_id}>
                {coach.full_name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Start date
          <input
            type="date"
            value={startDateFilter}
            onChange={(event) => setStartDateFilter(event.target.value)}
          />
        </label>
        <label>
          End date
          <input
            type="date"
            value={endDateFilter}
            onChange={(event) => setEndDateFilter(event.target.value)}
          />
        </label>
        <label>
          Level
          <select
            value={selectedLevel}
            onChange={(event) => setSelectedLevel(event.target.value)}
          >
            {LESSON_LEVELS.map((level) => (
              <option key={level} value={level}>
                {level}
              </option>
            ))}
          </select>
        </label>
        <label>
          Per page
          <select
            value={perPage}
            onChange={(event) => setPerPage(Number(event.target.value))}
          >
            <option value={15}>15</option>
            <option value={30}>30</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </label>
        <button type="button" className="upcoming-clear-button" onClick={clearFilters}>
          Clear
        </button>
      </div>
      {loading ? (
        <Spinner />
      ) : error ? (
        <div className="error">{error}</div>
      ) : (
        <>
          <div className="upcoming-table">
            <table>
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Coach</th>
                  <th>Start</th>
                  <th>End</th>
                  <th>Location</th>
                  <th>Price</th>
                  <th>Bookings</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {lessonsToDisplay.length ? (
                  lessonsToDisplay.map((lesson) => (
                    <tr key={lesson.id || lesson.external_id || lesson.start_date_time}>
                      <td>{getTitle(lesson)}</td>
                      <td>{getCoachName(lesson)}</td>
                      <td>{formatDateTime(getLessonStartValue(lesson))}</td>
                      <td>{formatDateTime(getLessonEndValue(lesson), getLessonStartValue(lesson))}</td>
                      <td>{getLocation(lesson)}</td>
                      <td>
                        {lesson.group_price_per_person !== undefined && lesson.group_price_per_person !== null
                          ? `$${lesson.group_price_per_person}`
                          : "-"}
                      </td>
                      <td>{lesson.external_url ? "-" : getParticipantSummary(lesson)}</td>
                      <td>{STATUS_OPTIONS.find((option) => option.value === getStatusValue(lesson.status))?.label || "-"}</td>
                      <td>
                        {lesson.external_url ? (
                          <a href={lesson.external_url} target="_blank" rel="noreferrer">
                            Link
                          </a>
                        ) : lesson.id ? (
                          <button
                            type="button"
                            className="upcoming-link-button"
                            onClick={() => openGroupLesson(lesson)}
                          >
                            View
                          </button>
                        ) : (
                          "-"
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9">No lessons found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {!loading && !error && (
            <Paginations
              perPage={perPage}
              total={activeTab === "group" ? totalGroupLessons : activeTab === "external" ? totalExternalLessons : totalGroupLessons + totalExternalLessons}
              paginate={handlePaginate}
              currentPage={currentPage}
            />
          )}
        </>
      )}
    </div>
    {selectedLesson && lessonForm && (
      <div className="upcoming-modal-backdrop">
        <div
          className="upcoming-modal"
          role="dialog"
          aria-modal="true"
          aria-label="View / Edit Group Lesson"
        >
          <form onSubmit={saveGroupLesson}>
            <div className="upcoming-modal-header">
              <div>
                <h3>View / Edit Group Lesson</h3>
                <p>{getCoachName(selectedLesson)} - Lesson #{selectedLesson.id}</p>
              </div>
              <button type="button" className="upcoming-close-button" onClick={closeGroupLesson}>
                Close
              </button>
            </div>

            {saveError && <div className="error">{saveError}</div>}

            {deleteConfirming && (
              <div className="upcoming-delete-warning" role="alert">
                <strong>Delete this group lesson?</strong>
                <p>
                  This removes it from upcoming lesson lists. The API will block deletion if any player has paid for
                  the lesson.
                </p>
              </div>
            )}

            <div className="upcoming-form-grid">
              <label>
                Title
                <input
                  type="text"
                  value={lessonForm.title}
                  onChange={(event) => handleFormChange("title", event.target.value)}
                />
              </label>

              <label>
                Description
                <textarea
                  rows="4"
                  value={lessonForm.description}
                  onChange={(event) => handleFormChange("description", event.target.value)}
                />
              </label>

              <label>
                Start
                <input
                  type="datetime-local"
                  value={lessonForm.start}
                  onChange={(event) => handleFormChange("start", event.target.value)}
                  required
                />
              </label>

              <label>
                End
                <input
                  type="datetime-local"
                  value={lessonForm.end}
                  onChange={(event) => handleFormChange("end", event.target.value)}
                  required
                />
              </label>

              <label>
                Level
                <select
                  value={lessonForm.level}
                  onChange={(event) => handleFormChange("level", event.target.value)}
                >
                  {LESSON_LEVELS.map((level) => (
                    <option key={level} value={level}>
                      {level}
                    </option>
                  ))}
                </select>
              </label>

              {Number(lessonForm.lessontype_id) === 4 && (
                <label>
                  Skill level required
                  <input
                    type="text"
                    value={lessonForm.skill_level_required}
                    onChange={(event) => handleFormChange("skill_level_required", event.target.value)}
                    required
                  />
                </label>
              )}

              <label>
                Duration (minutes)
                <input
                  type="number"
                  min="1"
                  value={lessonForm.duration}
                  onChange={(event) => handleFormChange("duration", event.target.value)}
                />
              </label>

              <label>
                Price per person
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={lessonForm.price_per_person}
                  onChange={(event) => handleFormChange("price_per_person", event.target.value)}
                  required
                />
              </label>

              <label>
                Player limit
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={lessonForm.player_limit}
                  onChange={(event) => handleFormChange("player_limit", event.target.value)}
                  required
                />
              </label>

              <label>
                Location ID
                <input
                  type="number"
                  value={lessonForm.location_id}
                  onChange={(event) => handleFormChange("location_id", event.target.value)}
                  required
                />
              </label>

              <label>
                Location
                <select
                  value={lessonForm.location_id}
                  onChange={(event) => handleLocationChange(event.target.value)}
                  required
                >
                  <option value="">Select location</option>
                  {locationOptions.map((location) => {
                    const id = getLocationId(location);
                    const name = getLocationName(location);

                    return (
                      <option key={id} value={id}>
                        {name || `Location #${id}`}
                      </option>
                    );
                  })}
                </select>
              </label>

              <label>
                Court
                <input
                  type="text"
                  value={lessonForm.court}
                  onChange={(event) => handleFormChange("court", event.target.value)}
                />
              </label>

              <label>
                Status
                <select
                  value={lessonForm.status}
                  onChange={(event) => handleFormChange("status", event.target.value)}
                >
                  {STATUS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {!selectedLesson.external_url && (
              <div className="upcoming-participants">
                <div className="upcoming-participants-header">
                  <h4>Participants</h4>
                  <span>{getParticipantSummary(selectedLesson)}</span>
                </div>
                {getGroupPlayers(selectedLesson).length ? (
                  <table>
                    <thead>
                      <tr>
                        <th>Player</th>
                        <th>Booking</th>
                        <th>Payment method</th>
                        <th>Payment status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getGroupPlayers(selectedLesson).map((player, index) => {
                        const state = getParticipantState(player);
                        const paymentStatus = getPlayerPaymentStatus(player);
                        const paymentMethod = getPlayerPaymentMethod(player);
                        const paymentStatusLabel =
                          paymentStatus === 1 || paymentStatus === "1"
                            ? "Paid"
                            : state.key === "comped"
                              ? "No payment due"
                            : state.paymentDue
                              ? "Due on court"
                              : paymentStatus ?? "-";

                        return (
                          <tr key={player.id || player.player_id || player.playerId || index}>
                            <td>
                              <strong>{getPlayerName(player, index)}</strong>
                              {(player.email || player.player_email) && (
                                <small>{player.email || player.player_email}</small>
                              )}
                            </td>
                            <td>
                              <span className={`upcoming-payment-badge upcoming-payment-badge--${state.tone}`}>
                                {state.label}
                              </span>
                              {state.paymentDue && (
                                <small className="upcoming-payment-due">Collect on lesson day</small>
                              )}
                            </td>
                            <td>{paymentMethod || "-"}</td>
                            <td>{paymentStatusLabel}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ) : (
                  <p>No participants yet.</p>
                )}
              </div>
            )}

            <div className="upcoming-modal-actions">
              <div className="upcoming-delete-actions">
                {deleteConfirming ? (
                  <>
                    <button type="button" onClick={() => setDeleteConfirming(false)} disabled={deleting}>
                      Keep Lesson
                    </button>
                    <button
                      type="button"
                      className="upcoming-delete-button"
                      onClick={deleteGroupLesson}
                      disabled={deleting}
                    >
                      {deleting ? "Deleting..." : "Confirm Delete"}
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="upcoming-delete-button"
                    onClick={() => setDeleteConfirming(true)}
                    disabled={saving || deleting}
                  >
                    Delete
                  </button>
                )}
              </div>
              <div className="upcoming-save-actions">
                <button type="button" onClick={closeGroupLesson} disabled={saving || deleting}>
                  Cancel
                </button>
                <button type="submit" disabled={saving || deleting}>
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    )}
  </main>);
};

export default UpcomingLessons;
